/** CRUD Expert dashboard shell — aligned with Dashboard v4 reference. */
export const DFT_BRAND = {
  railStart: '#101d3b',
  railEnd: '#101a31',
  navText: '#b2c0d9',
  navMuted: '#7e91b5',
  navHover: 'rgba(255, 255, 255, 0.043)',
  navSelected: 'linear-gradient(100deg, #3570e8, #4f74e5)',
  navSelectedShadow: '0 5px 15px rgba(7, 17, 39, 0.33)',
  paper: '#ffffff',
  bg: '#f3f6fb',
  text: '#172441',
  muted: '#64748d',
  line: '#e7edf5',
  tint: '#edf3ff',
  primaryStart: '#326bda',
  primaryEnd: '#5279e8',
  primaryShadow: '0 4px 10px rgba(57, 116, 214, 0.13)',
  eyebrow: '#428e9b',
  usage: '#269995',
  link: '#427bdd',
  icon: '#527de0',
  avatarBg: '#edf3ff',
  avatarColor: '#4d79d5',
  methodBg: '#e8f7f0',
  methodColor: '#118467',
  focus: '#6c9dff',
  metricAccent: 'linear-gradient(90deg, #4c83ec, rgba(64, 186, 182, 0.2))',
  chartBlue: '#467ee8',
  chartTeal: '#21afbe',
  chartOrange: '#f39550',
  chartGold: '#cfad36',
  chartGreen: '#48a882',
  chartPurple: '#9471d9',
} as const;

export const SIDEBAR_COLLAPSED_WIDTH_PX = 68;
export const SIDEBAR_MOBILE_WIDTH = 'min(260px, 85vw)';
export const HEADER_HEIGHT_PX = 64;

/** Expanded rail width by MUI breakpoint (md 900 / lg 1200 / xl 1536). */
export const SIDEBAR_EXPANDED_WIDTH = {
  md: 200,
  lg: 208,
  xl: 220,
} as const;

/** Default expanded rail — matches lg. */
export const SIDEBAR_EXPANDED_WIDTH_PX = SIDEBAR_EXPANDED_WIDTH.lg;

export function sidebarExpandedWidthPx(viewportWidth: number): number {
  if (viewportWidth >= 1536) return SIDEBAR_EXPANDED_WIDTH.xl;
  if (viewportWidth >= 1200) return SIDEBAR_EXPANDED_WIDTH.lg;
  return SIDEBAR_EXPANDED_WIDTH.md;
}

/**
 * CSS `zoom` uses **CSS pixels** (`window.innerWidth` × `innerHeight`).
 * Windows 125/150% scaling and Mac Retina already change those numbers — do not
 * also key off `devicePixelRatio`.
 *
 * Width bands (after OS scale):
 *   1366×768 Windows laptop, 1080p @ 150% → 1280
 *   MacBook Air 13" → 1440; MacBook Pro 14" → 1512; 1080p @ 125% → 1536
 *   MacBook Pro 16" → 1728
 *   1080p desktop 100% / 4K @ 200% → 1920
 *   27" / Studio / 4K @ 150% → 2560
 *
 * Short heights (768p laptops, 1080p @ 150%, browser chrome) shrink a bit more
 * so chrome fits without making MacBooks look tiny.
 */
export const PAGE_ZOOM_XXL_PX = 1920;
export const PAGE_ZOOM_QHD_PX = 2560;
export const APP_PAGE_ZOOM_VAR = 'var(--app-page-zoom, 1)';
/** Layout px the 64px header occupies after the same CSS zoom as the rail/page. */
export const APP_HEADER_LAYOUT_HEIGHT = `calc(${HEADER_HEIGHT_PX}px * ${APP_PAGE_ZOOM_VAR})`;

/** Width-only approximation (height-aware value lives in `--app-page-zoom`). */
export const APP_PAGE_ZOOM = {
  xs: 1,
  sm: 1,
  md: 0.84,
  lg: 0.82,
  xl: 0.84,
  xxl: 0.88,
} as const;

export const PAGE_ZOOM = APP_PAGE_ZOOM;
export const COLLECTIONS_PAGE_ZOOM = APP_PAGE_ZOOM;

export const APP_PAGE_ZOOM_LG = APP_PAGE_ZOOM.lg;
export const APP_PAGE_ZOOM_XL = APP_PAGE_ZOOM.xl;
export const COLLECTIONS_PAGE_ZOOM_LG = APP_PAGE_ZOOM.lg;
export const COLLECTIONS_PAGE_ZOOM_XL = APP_PAGE_ZOOM.xl;

export type PageZoomScale = {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
  xxl: number;
};

export function resolveAppPageZoom(width: number, height: number): number {
  if (width < 900) return 1;

  let zoom: number;
  if (width >= PAGE_ZOOM_QHD_PX) zoom = 0.92;
  else if (width >= PAGE_ZOOM_XXL_PX) zoom = 0.88;
  else if (width >= 1680) zoom = 0.86;
  else if (width >= 1440) zoom = 0.84;
  else if (width >= 1280) zoom = 0.82;
  else zoom = 0.84;

  if (height > 0) {
    if (height < 740) zoom = Math.min(zoom, 0.78);
    else if (height < 800) zoom = Math.min(zoom, 0.8);
    else if (height < 880) zoom = Math.min(zoom, 0.82);
  }

  return Number(zoom.toFixed(2));
}

export function pageZoomSx(_scale?: PageZoomScale) {
  return APP_PAGE_ZOOM_VAR;
}

export function pageZoomAtViewport(
  _scale: PageZoomScale,
  viewportWidth: number,
  viewportHeight: number = typeof window !== 'undefined' ? window.innerHeight : 900
): number {
  return resolveAppPageZoom(viewportWidth, viewportHeight);
}

/** Docked rail width after CSS zoom. */
export function zoomedSidebarWidthSx(_expanded?: boolean) {
  return 'var(--app-sidebar-width)';
}

export function sidebarPaperHeightForZoomSx() {
  return {
    xs: '100dvh',
    md: `calc(100dvh / ${APP_PAGE_ZOOM_VAR})`,
  };
}

/** Pixel width the docked rail occupies after app CSS zoom. */
export function getSidebarWidthPx(
  open: boolean,
  viewportWidth: number = typeof window !== 'undefined' ? window.innerWidth : 1440,
  viewportHeight: number = typeof window !== 'undefined' ? window.innerHeight : 900
): number {
  if (viewportWidth < 900) return 0;
  const base = !open ? SIDEBAR_COLLAPSED_WIDTH_PX : sidebarExpandedWidthPx(viewportWidth);
  return Math.round(base * resolveAppPageZoom(viewportWidth, viewportHeight));
}

export function applyAppViewportCssVars(sidebarOpen: boolean): number {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 1;
  const width = window.innerWidth;
  const height = window.innerHeight;
  const zoom = resolveAppPageZoom(width, height);
  const root = document.documentElement;
  root.style.setProperty('--app-page-zoom', String(zoom));
  root.style.setProperty('--app-sidebar-width', `${getSidebarWidthPx(sidebarOpen, width, height)}px`);
  return zoom;
}

export function zoomedMainMinHeight(_scale?: PageZoomScale, padPx = 0) {
  const h = padPx
    ? `calc(100vh / ${APP_PAGE_ZOOM_VAR} - ${HEADER_HEIGHT_PX}px - ${padPx}px)`
    : `calc(100vh / ${APP_PAGE_ZOOM_VAR} - ${HEADER_HEIGHT_PX}px)`;
  return {
    md: h,
    lg: h,
    xl: h,
    [`@media (min-width:${PAGE_ZOOM_XXL_PX}px)`]: h,
  };
}
